import { Alert, Box, Button, HStack, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { ConfirmDialog, LoadingState } from '@mangovoice/mango-ui'

import type { PbxSettingsApi } from '../api/pbxSettingsApi.types'
import { PbxSettingsAdvancedSection } from '../components/PbxSettingsAdvancedSection'
import { PbxSettingsCoreSection } from '../components/PbxSettingsCoreSection'
import { PbxSettingsSystemSettingsSection } from '../components/PbxSettingsSystemSettingsSection'
import { usePbxSettingsQueries } from '../queries'
import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type {
  EditPbxFormState,
  PbxFormValues,
  SelectOption,
  SystemSettingsFormValues,
} from '../types/pbxSettings.types'
import {
  CALL_PARK_TIMEOUT_WARN_THRESHOLD,
  DISABLED_CODEC_OPTIONS,
  KNOWN_CODEC_OPTIONS,
} from '../utils/pbxSettings.constants'
import {
  buildInitialFormState,
  buildPbxPatchPayload,
  buildSystemSettingsPatchPayload,
  cloneFormState,
  getChangedPayload,
  resolvePhoneNumberValue,
  stringifyError,
} from '../utils/pbxSettings.utils'
import { validateEditPbxForm } from '../utils/pbxSettings.validation'

export interface PbxSettingsPageProps {
  api: PbxSettingsApi
  pbxId: number | undefined
  toast: ToastLike
  isInternalUser: boolean
  onRebuildAndRebootDevices?: (pbxId: number) => Promise<void>
  t?: PbxSettingsTranslateFn
}

export const PbxSettingsPage = ({
  api,
  pbxId,
  toast,
  isInternalUser,
  onRebuildAndRebootDevices,
  t,
}: PbxSettingsPageProps) => {
  const queryClient = useQueryClient()

  const [formState, setFormState] = useState<EditPbxFormState | null>(null)
  const [initialFormState, setInitialFormState] =
    useState<EditPbxFormState | null>(null)
  const [isCallParkTimeoutConfirmOpen, setIsCallParkTimeoutConfirmOpen] =
    useState(false)
  const [isRebuildAndRebootConfirmOpen, setIsRebuildAndRebootConfirmOpen] =
    useState(false)
  const [callParkTimeoutWarningAck, setCallParkTimeoutWarningAck] =
    useState(false)
  const [companyPageName, setCompanyPageName] = useState<string | undefined>(
    undefined,
  )

  const queries = usePbxSettingsQueries(api, pbxId, t)

  // Ensure form state is rehydrated when navigating between PBX IDs on the same route instance.
  useEffect(() => {
    setFormState(null)
    setInitialFormState(null)
    setIsCallParkTimeoutConfirmOpen(false)
    setIsRebuildAndRebootConfirmOpen(false)
    setCallParkTimeoutWarningAck(false)
  }, [pbxId])

  useEffect(() => {
    if (
      !queries.pbxQuery.data ||
      !queries.systemSettingsQuery.data ||
      formState
    )
      return

    const initial = buildInitialFormState(
      queries.pbxQuery.data,
      queries.systemSettingsQuery.data,
    )
    setFormState(initial)
    setInitialFormState(cloneFormState(initial))
  }, [queries.pbxQuery.data, queries.systemSettingsQuery.data, formState])

  // Reconcile phone number values once phone data loads.
  // Functional setter avoids needing formState as a dependency.
  useEffect(() => {
    if (!queries.phoneNumbers?.length) return
    const phoneNumbers = queries.phoneNumbers

    setFormState((current) => {
      if (!current) return current
      const nextCallerId = resolvePhoneNumberValue(
        current.systemSettings.default_caller_id,
        phoneNumbers,
      )
      const nextFaxNumber = resolvePhoneNumberValue(
        current.systemSettings.default_send_fax_number,
        phoneNumbers,
      )
      if (
        nextCallerId === current.systemSettings.default_caller_id &&
        nextFaxNumber === current.systemSettings.default_send_fax_number
      ) {
        return current
      }
      return {
        ...current,
        systemSettings: {
          ...current.systemSettings,
          default_caller_id: nextCallerId,
          default_send_fax_number: nextFaxNumber,
        },
      }
    })
  }, [queries.phoneNumbers])

  const patchPbxMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { ok, data } = await api.patchPbxV2(pbxId ?? 0, payload)
      if (!ok)
        throw new Error(stringifyError(data, 'Failed to update PBX settings.'))
      return data
    },
  })

  const patchSystemSettingsMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { ok, data } = await api.patchSystemSettingsV2(pbxId ?? 0, payload)
      if (!ok)
        throw new Error(
          stringifyError(data, 'Failed to update system settings.'),
        )
      return data
    },
  })

  const rebuildAndRebootDevicesMutation = useMutation({
    mutationFn: async () => {
      if (!pbxId || !onRebuildAndRebootDevices) {
        throw new Error(
          text(
            t,
            'pbxSettings.rebuildAndReboot.toast.errorDescription',
            'There was an issue rebuilding and rebooting devices.',
          ),
        )
      }
      await onRebuildAndRebootDevices(pbxId)
    },
  })

  const codecSelectOptions = useMemo<SelectOption[]>(() => {
    const options = new Set(KNOWN_CODEC_OPTIONS)
    if (formState?.pbx.preferred_codec)
      options.add(formState.pbx.preferred_codec)
    return [...options].map((c) => ({ value: c, label: c }))
  }, [formState?.pbx.preferred_codec])

  const handlePbxFieldChange = <K extends keyof PbxFormValues>(
    field: K,
    value: PbxFormValues[K],
  ) => {
    setFormState((current) => {
      if (!current) return current
      return { ...current, pbx: { ...current.pbx, [field]: value } }
    })
  }

  const handleSystemSettingsFieldChange = <
    K extends keyof SystemSettingsFormValues,
  >(
    field: K,
    value: SystemSettingsFormValues[K],
  ) => {
    setFormState((current) => {
      if (!current) return current
      if (field === 'call_park_timeout') setCallParkTimeoutWarningAck(false)
      return {
        ...current,
        systemSettings: { ...current.systemSettings, [field]: value },
      }
    })
  }

  const isSaving =
    patchPbxMutation.isPending || patchSystemSettingsMutation.isPending

  const handleSave = async (
    skipCallParkTimeoutWarningConfirm: boolean = false,
  ) => {
    if (!formState || !initialFormState || !pbxId) return

    const validationError = validateEditPbxForm(formState, t)
    if (validationError) {
      toast.create({
        title: text(t, 'pbxSettings.toast.validationError', 'Validation error'),
        description: validationError,
        type: 'error',
        duration: 5000,
      })
      return
    }

    const callParkTimeout = Number.parseInt(
      formState.systemSettings.call_park_timeout.trim(),
      10,
    )
    if (
      callParkTimeout > CALL_PARK_TIMEOUT_WARN_THRESHOLD &&
      !callParkTimeoutWarningAck &&
      !skipCallParkTimeoutWarningConfirm
    ) {
      setIsCallParkTimeoutConfirmOpen(true)
      return
    }

    if (
      formState.systemSettings.default_time_zone !==
      initialFormState.systemSettings.default_time_zone
    ) {
      toast.create({
        title: text(
          t,
          'pbxSettings.toast.timezoneChanged.title',
          'Timezone changed',
        ),
        description: text(
          t,
          'pbxSettings.toast.timezoneChanged.description',
          'You changed the PBX timezone. Please remember to update your schedules accordingly.',
        ),
        type: 'warning',
        duration: 5000,
      })
    }

    const initialPbxPayload = buildPbxPatchPayload(initialFormState.pbx)
    const currentPbxPayload = buildPbxPatchPayload(formState.pbx)
    const pbxPayload = getChangedPayload(initialPbxPayload, currentPbxPayload)

    const initialSystemSettingsPayload = buildSystemSettingsPatchPayload(
      initialFormState.systemSettings,
    )
    const currentSystemSettingsPayload = buildSystemSettingsPatchPayload(
      formState.systemSettings,
    )
    const systemSettingsPayload = getChangedPayload(
      initialSystemSettingsPayload,
      currentSystemSettingsPayload,
    )

    try {
      if (Object.keys(pbxPayload).length > 0) {
        await patchPbxMutation.mutateAsync(pbxPayload)
      }
      if (Object.keys(systemSettingsPayload).length > 0) {
        await patchSystemSettingsMutation.mutateAsync(systemSettingsPayload)
      }

      setInitialFormState(cloneFormState(formState))
      toast.create({
        title: text(t, 'pbxSettings.toast.saveSuccess', 'PBX settings updated'),
        type: 'success',
        duration: 3500,
      })
      if (isInternalUser && onRebuildAndRebootDevices) {
        setIsRebuildAndRebootConfirmOpen(true)
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pbxV2Edit', pbxId] }),
        queryClient.invalidateQueries({
          queryKey: ['pbxSystemSettingsV2', pbxId],
        }),
        queryClient.invalidateQueries({ queryKey: ['pbxV2', pbxId] }),
      ])
    } catch (error) {
      toast.create({
        title: text(t, 'pbxSettings.toast.saveError.title', 'Update failed'),
        description:
          error instanceof Error
            ? error.message
            : text(
                t,
                'pbxSettings.toast.saveError.description',
                'There was an issue updating PBX settings.',
              ),
        type: 'error',
        duration: 6000,
      })
    }
  }

  const handleRebuildAndRebootConfirm = () => {
    rebuildAndRebootDevicesMutation.mutate(undefined, {
      onSuccess: () => {
        setIsRebuildAndRebootConfirmOpen(false)
        toast.create({
          title: text(
            t,
            'pbxSettings.rebuildAndReboot.toast.successTitle',
            'Rebuild and reboot started',
          ),
          description: text(
            t,
            'pbxSettings.rebuildAndReboot.toast.successDescription',
            'All devices are being rebuilt and rebooted.',
          ),
          type: 'success',
          duration: 5000,
        })
      },
      onError: (error) => {
        setIsRebuildAndRebootConfirmOpen(false)
        toast.create({
          title: text(
            t,
            'pbxSettings.rebuildAndReboot.toast.errorTitle',
            'Rebuild and reboot failed',
          ),
          description: stringifyError(
            error,
            text(
              t,
              'pbxSettings.rebuildAndReboot.toast.errorDescription',
              'There was an issue rebuilding and rebooting devices.',
            ),
          ),
          type: 'error',
          duration: 5000,
        })
      },
    })
  }

  const handleCallParkTimeoutConfirm = () => {
    setCallParkTimeoutWarningAck(true)
    setIsCallParkTimeoutConfirmOpen(false)
    void handleSave(true)
  }

  const handleCallParkTimeoutCancel = () => {
    if (!initialFormState) {
      setIsCallParkTimeoutConfirmOpen(false)
      return
    }

    const initialTimeout = Number.parseInt(
      initialFormState.systemSettings.call_park_timeout.trim(),
      10,
    )
    const resetTo =
      Number.isFinite(initialTimeout) &&
      initialTimeout > 0 &&
      initialTimeout <= CALL_PARK_TIMEOUT_WARN_THRESHOLD
        ? initialTimeout
        : CALL_PARK_TIMEOUT_WARN_THRESHOLD

    handleSystemSettingsFieldChange('call_park_timeout', String(resetTo))
    setIsCallParkTimeoutConfirmOpen(false)
  }

  if (!pbxId) {
    return (
      <Alert.Root status="warning" p={4}>
        <Alert.Indicator />
        <Box>
          <Text fontWeight="bold">
            {text(t, 'pbxSettings.invalidPbx.title', 'Invalid PBX')}
          </Text>
          <Text>
            {text(
              t,
              'pbxSettings.invalidPbx.description',
              'A valid PBX ID is required to edit PBX settings. Please select a PBX from the dashboard.',
            )}
          </Text>
        </Box>
      </Alert.Root>
    )
  }

  if (
    queries.pbxQuery.isLoading ||
    queries.systemSettingsQuery.isLoading ||
    !formState
  ) {
    return (
      <LoadingState
        layout="alert"
        title={text(t, 'pbxSettings.loading', 'Loading PBX settings...')}
      />
    )
  }

  if (queries.pbxQuery.error || queries.systemSettingsQuery.error) {
    return (
      <Alert.Root status="error" p={4}>
        <Alert.Indicator />
        <Box>
          <Text fontWeight="bold">
            {text(
              t,
              'pbxSettings.loadError.title',
              'Unable to load PBX settings',
            )}
          </Text>
          <Text>
            {queries.pbxQuery.error?.message ??
              queries.systemSettingsQuery.error?.message ??
              text(t, 'pbxSettings.loadError.description', 'Please try again.')}
          </Text>
        </Box>
      </Alert.Root>
    )
  }

  return (
    <Stack gap={6} maxW="4xl" mx="auto" width="full">
      {queries.secondaryQueryErrors.length > 0 && (
        <Alert.Root status="warning" p={4}>
          <Alert.Indicator />
          <Box>
            <Text fontWeight="bold">
              {text(
                t,
                'pbxSettings.partialLoadWarning.title',
                'Some data failed to load',
              )}
            </Text>
            {queries.secondaryQueryErrors.map((err: string) => (
              <Text key={err} fontSize="sm">
                {err}
              </Text>
            ))}
          </Box>
        </Alert.Root>
      )}

      <PbxSettingsCoreSection
        api={api}
        t={t}
        pbx={formState.pbx}
        selectedCompanyName={queries.pbxQuery.data?.company_name ?? undefined}
        companyPageName={companyPageName}
        onResolvedCompany={(company) =>
          setCompanyPageName((current) =>
            current === (company?.name ?? undefined)
              ? current
              : (company?.name ?? undefined),
          )
        }
        outboundProviderGatewayOptions={queries.outboundGatewayOptions}
        emergencyProviderGatewayOptions={queries.emergencyGatewayOptions}
        internationalProviderGatewayOptions={
          queries.internationalGatewayOptions
        }
        onPbxFieldChange={handlePbxFieldChange}
      />

      <PbxSettingsAdvancedSection
        t={t}
        pbx={formState.pbx}
        serverGroupOptions={queries.serverGroupOptions}
        isInternalUser={isInternalUser}
        onPbxFieldChange={handlePbxFieldChange}
      />

      <PbxSettingsSystemSettingsSection
        api={api}
        t={t}
        pbxId={pbxId}
        pbx={formState.pbx}
        systemSettings={formState.systemSettings}
        isInternalUser={isInternalUser}
        alert911Options={queries.alert911Options}
        timezoneOptions={queries.timezoneOptions}
        virtualFaxOptions={queries.virtualFaxOptions}
        codecSelectOptions={codecSelectOptions}
        disabledCodecValues={DISABLED_CODEC_OPTIONS}
        virtualFaxLoading={queries.virtualFaxLoading}
        onPbxFieldChange={handlePbxFieldChange}
        onSystemSettingsFieldChange={handleSystemSettingsFieldChange}
      />

      <HStack justify="end" gap={3}>
        <Button
          colorPalette="brand"
          rounded="lg"
          onClick={() => {
            void handleSave()
          }}
          loading={isSaving}
        >
          {text(t, 'pbxSettings.actions.update', 'Update')}
        </Button>
      </HStack>
      <ConfirmDialog
        open={isCallParkTimeoutConfirmOpen}
        onClose={handleCallParkTimeoutCancel}
        onConfirm={handleCallParkTimeoutConfirm}
        title={text(
          t,
          'pbxSettings.callParkConfirm.title',
          'Confirm Call Park Timeout',
        )}
        description={text(
          t,
          'pbxSettings.callParkConfirm.description',
          'Are you sure you want to keep callers in the parking lot for longer than 10 minutes?',
        )}
        cancelLabel={text(
          t,
          'pbxSettings.callParkConfirm.cancel',
          'Reset timeout',
        )}
        confirmLabel={text(
          t,
          'pbxSettings.callParkConfirm.confirm',
          'Continue',
        )}
      />
      <ConfirmDialog
        open={isRebuildAndRebootConfirmOpen}
        onClose={() => setIsRebuildAndRebootConfirmOpen(false)}
        onConfirm={handleRebuildAndRebootConfirm}
        title={text(
          t,
          'pbxSettings.rebuildAndReboot.title',
          'PBX settings updated',
        )}
        description={text(
          t,
          'pbxSettings.rebuildAndReboot.description',
          'Some PBX settings may not take effect on devices until they have been rebuilt and rebooted.',
        )}
        cancelLabel={text(t, 'pbxSettings.rebuildAndReboot.cancel', 'Not now')}
        confirmLabel={text(
          t,
          'pbxSettings.rebuildAndReboot.confirm',
          'Rebuild and Reboot Devices',
        )}
        confirmColorPalette="brand"
        isLoading={rebuildAndRebootDevicesMutation.isPending}
      />
    </Stack>
  )
}
